import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IncomeVerificationType } from '@brighte/validation';
import { useOpenBankingSteps } from '@provider';
import { Stack } from '@spark-web/stack';
import { ManualOccupationJobTitleMatchingType } from '../../utils';
import AdditionalEmploymentFields from './AdditionalEmploymentFields';
import IncomeFields from './IncomeFields';

interface ManualIncomeFieldsProps {
  formContext: UseFormReturn<IncomeVerificationType>;
  financialProductId: string;
  isOpenBankingOptionEnabled: boolean;
  formFields: string[];
  manualOccupationJobTitle: ManualOccupationJobTitleMatchingType;
  setManualOccupationJobTitle: React.Dispatch<React.SetStateAction<ManualOccupationJobTitleMatchingType>>;
  incomeVerification?: IncomeVerificationType;
  occupationIncomeThreshold: number;
  setOccupationIncomeThreshold: React.Dispatch<React.SetStateAction<number>>;
  suppressVerificationCta?: boolean;
  showAdditionalIncomeToggle?: boolean;
  primaryUploadModalDisclosure?: { isOpen: boolean; onOpen: () => void; onToggle: () => void };
}

const ManualIncomeFields = ({
  formContext,
  financialProductId,
  isOpenBankingOptionEnabled,
  formFields,
  manualOccupationJobTitle,
  setManualOccupationJobTitle,
  incomeVerification,
  occupationIncomeThreshold,
  setOccupationIncomeThreshold,
  suppressVerificationCta = false,
  showAdditionalIncomeToggle = false,
  primaryUploadModalDisclosure,
}: ManualIncomeFieldsProps) => {
  const { isRequestingManualIncomeEdit } = useOpenBankingSteps();
  const { watch, setValue } = formContext;
  const [isAdditionalIncome, usingOpenBanking] = watch([
    'applicant.income.isAdditionalIncome',
    'applicant.usingOpenBanking',
  ]);
  const resolvedUsingOpenBanking = usingOpenBanking ?? incomeVerification?.applicant.usingOpenBanking;

  useEffect(() => {
    if (isRequestingManualIncomeEdit) {
      setValue('applicant.usingOpenBanking', false);
    }
  }, [isRequestingManualIncomeEdit, isOpenBankingOptionEnabled]);

  const shouldRender =
    !isOpenBankingOptionEnabled || isRequestingManualIncomeEdit || resolvedUsingOpenBanking === false;
  if (!shouldRender) return null;

  return (
    <Stack gap="xlarge" margin="xsmall">
      <AdditionalEmploymentFields
        usingOpenBanking={isOpenBankingOptionEnabled ? resolvedUsingOpenBanking : false}
        financialProductId={financialProductId}
        setOccupationIncomeThreshold={setOccupationIncomeThreshold}
        manualOccupationJobTitle={manualOccupationJobTitle}
        setManualOccupationJobTitle={setManualOccupationJobTitle}
      />
      <IncomeFields
        incomeType="primary"
        financialProductId={financialProductId}
        formFields={formFields}
        occupationIncomeThreshold={occupationIncomeThreshold}
        suppressVerificationCta={suppressVerificationCta}
        showAdditionalIncomeToggle={showAdditionalIncomeToggle}
        uploadModalDisclosure={primaryUploadModalDisclosure}
      />

      {isAdditionalIncome && (!suppressVerificationCta || showAdditionalIncomeToggle) && (
        <IncomeFields
          incomeType="additional"
          financialProductId={financialProductId}
          formFields={formFields}
          suppressVerificationCta={suppressVerificationCta}
        />
      )}
    </Stack>
  );
};

export default ManualIncomeFields;
